import { useForm, type FieldValues, type Path, type UseFormProps, type UseFormReturn } from 'react-hook-form';
import { useNavigate } from 'react-router';
import type { BaseSyntheticEvent } from 'react';
import type { IApiSubmitResultBase } from '@/types/api';
import type { DashRegistrationStep } from '@/types/dashboard';

export interface IStepSubmitResult extends IApiSubmitResultBase {
    nextStep?: DashRegistrationStep;
}

export interface IStepLink {
    step: DashRegistrationStep;
    link: string;
}

export interface IUseStepFormOptions<TValues extends FieldValues, TResult extends IStepSubmitResult> extends UseFormProps<TValues> {
    submit: (values: TValues) => Promise<TResult>;
    linkMap: IStepLink[];
    onSuccess?: (result: TResult, values: TValues) => void;
}

export interface IUseStepFormReturn<TValues extends FieldValues> extends UseFormReturn<TValues> {
    onSubmit: (e?: BaseSyntheticEvent) => Promise<void>;
}

export const useStepForm = <TValues extends FieldValues, TResult extends IStepSubmitResult>({
    submit,
    linkMap,
    onSuccess,
    ...formProps
}: IUseStepFormOptions<TValues, TResult>): IUseStepFormReturn<TValues> => {
    const form = useForm<TValues>(formProps);
    const navigate = useNavigate();

    const onValid = async (values: TValues) => {
        const result = await submit(values);

        if (!result.ok) {
            Object.entries(result.errors ?? {}).forEach(([field, message]) => {
                form.setError(field as Path<TValues>, { type: 'server', message });
            });

            return;
        }

        onSuccess?.(result, values);

        const nextLink = result.nextStep != null ? linkMap.find((x) => x.step === result.nextStep) : undefined;

        if (nextLink) {
            navigate(nextLink.link);
        }
    };

    return { ...form, onSubmit: form.handleSubmit(onValid) };
};
